import get from 'lodash/get';
import type { Saga } from 'redux-saga';
import { call, put, select, take } from 'typed-redux-saga/macro';

import { getRoute } from 'Config';
import { I13N_CATEGORY_API, I13N_CATEGORY_SAGA, I13N_TYPE_EXCEPTION } from 'Lib/constants/i13nSpec';
import { debugShowError } from 'Lib/debug';
import {
  AppealableBannedUserError,
  BadRequestAPIError,
  BANNED_USER_ERROR,
  BannedUserError,
  BLACKLISTED_USER_ERROR,
  BlacklistedUserError,
  BrowserNetworkError,
  LoggedOutFacebookError,
  LoggedOutTinderError,
  RateLimitedError,
  RequestTimeoutError,
  RSO_BANNED_USER_ERROR,
  RSOBannedUserError,
  SelfieChallengeV2Error,
  ServerSideAPIError,
  UnauthorizedAPIError,
  UNDER_REVIEW_USER_ERROR,
  UnderageUserError,
  UnderReviewUserError,
} from 'Lib/errors';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { getUrlPathName } from 'Lib/utils/pathUtils';

import { appOffline, appOnline } from 'Store/app/actions';
import { getIsOffline } from 'Store/app/selectors';
import { BanPolicy } from 'Store/appeals/types';
import { logout } from 'Store/commonActions';
import { getIsRemote } from 'Store/context/selectors';
import { reauth } from 'Store/mfa/actions';
import { LOGIN_SUCCESS } from 'Store/mfa/constants';
import { getIsReauthing } from 'Store/mfa/selectors';
import { getIsUserLoggedIn, getIsUserLoggingOut } from 'Store/user/selectors';

import { LEVER_DSA_CUSTOM_BAN_SCREEN } from './ConfigService/levers';
import { getLeverValue } from './utils/getLeverValue';

const { executeEvent, executeLogEvent, Log } = getI13nUtils();

function trackErrorResponse({ url, status }) {
  if (status === 429) {
    // don't track backend internal rate limiter
    return;
  }
  const params = {
    category: I13N_CATEGORY_API,
    label: status,
    message: getUrlPathName(url),
    sampleRate: 0.01,
  };
  executeEvent(I13N_TYPE_EXCEPTION, params);
}

export const isAuthError = (e) =>
  e instanceof UnauthorizedAPIError ||
  e instanceof LoggedOutFacebookError ||
  e instanceof LoggedOutTinderError;

const isTimeoutError = (e) => e instanceof RequestTimeoutError;
const isAPIError = (e) => e instanceof ServerSideAPIError;

type WithErrorHandlingOptions<T extends any[]> = {
  errorHandler?: Saga<[e: unknown, ...args: T]> | ((e: unknown, ...args: T) => any);
  errorSampleRate?: number;
  finalHandler?: Saga<T>;
  logLevel?: any;
  name?: string;
  shouldLogError?: (...payload: any[]) => boolean;
};

/**
 * Wrapper function to catch error from saga
 * @param  {generator}  saga                   saga to wrap
 * @param  {generator}  errorHandler           customize catch condition if there is error
 * @param  {generator}  finalHandler           customize finally condition
 * @param  {string}     name                   saga name
 * @param  {Boolean} [handleGenericError=true] optional opts in for error logging, offine handling and auth error
 * @return {generator}                         wrapped generator
 */
export function withErrorHandling<T extends any[]>(
  saga: (...args: any[]) => Generator<any, void, any>,
  {
    errorHandler,
    finalHandler,
    name,
    errorSampleRate,
    logLevel,
    shouldLogError,
  }: WithErrorHandlingOptions<T> = {},
  handleGenericError = true
): any {
  return function* wrappedSaga(...args: T) {
    let result;
    const displayName = name || saga.name;
    try {
      result = yield* call(saga, ...args);
    } catch (e: any) {
      if (e) {
        debugShowError(`${displayName} - %O`, e.message || e);
      }
      // custom handler
      if (errorHandler) {
        yield* call(errorHandler, e, ...args);
      }
      // generic handler
      if (handleGenericError) {
        yield* call(handleOffline, e);
        yield* call(handleAuthError, e);

        if ((!isAuthError(e) && !isTimeoutError(e) && !isAPIError(e)) || shouldLogError?.(e)) {
          logException(e, displayName, logLevel, errorSampleRate);
        }
      }
    } finally {
      if (finalHandler) {
        yield* call(finalHandler, ...args);
      }
    }
    return result;
  };
}

function* handleOffline(e) {
  if (isTimeoutError(e)) {
    const isOffline = yield* select(getIsOffline);
    if (!isOffline) {
      yield* put(appOffline());
    }
  }
}

export function* handleAuthError(e) {
  const isLoggingOut = yield* select(getIsUserLoggingOut);
  if (isAuthError(e) && !isLoggingOut) {
    yield* put(logout(false));
  }
}

export function logException(errorObject, displayName, level, sampleRate) {
  const code = get(errorObject, ['error', 'code']);
  const errorMessage = get(errorObject, ['error', 'message']);

  // skip
  // 1. API 400 error which we already fire event under API category
  // should probably throw another error so we don't mix the logic
  // 2. fetch network error
  if (
    (errorObject instanceof BadRequestAPIError && !code) ||
    errorObject instanceof BrowserNetworkError
  ) {
    return;
  }

  const message = [
    displayName,
    errorObject instanceof Error ? errorObject.toString() : JSON.stringify(errorObject),
    code, // (BadRequestAPIError) unhandled error status code from backend
    errorMessage,
  ]
    .filter(Boolean)
    .join(' - ');

  executeEvent(I13N_TYPE_EXCEPTION, {
    category: I13N_CATEGORY_SAGA,
    message,
    error: {
      error: errorObject,
      level,
      tags: [displayName],
    },
    sampleRate,
  });
}

const UNDERAGE_USER_CREATION_ERROR_CODE = 40802; // On v2/onboarding/complete
// 403xx banned
const UNDERAGE_AUTH_ERROR_CODE = 40301; // On auth call
const UNDERAGE_ONBOARDING_ERROR_CODE = 40321; // On v2/onboarding/update if previous underage DOB
const UNDERAGE_ONBOARDING_NOT_APPEALABLE_CODE = 40344;
const BLACKLIST_ERROR_CODE = 40303;
const BANNED_ERROR_CODE = 40305;
const UNDER_REVIEW_CODE = 40306;
const APPEALABLE_BANNED_CODE = 40307;
const RSO_BANNED_CODE = 40308;
const RATE_LIMITED_CODE = 42901;
const SELFIE_CHALLENGE_V2_ERROR_CODE = 40324;

export function* handleResponseErrors(response, status) {
  const error = get(response, 'error');

  const errorCode = get(response, ['error', 'code']);
  const appealToken = response?.error?.banReason?.banAppeal?.challengeToken ?? null;
  const selfieChallengeToken =
    response?.error?.banReason?.selfieChallengeBan?.selfieChallengeToken ?? null;
  const selfieStatus = response?.error?.banReason?.selfieChallengeBan?.selfieStatus ?? null;
  const policy = response?.error?.banReason?.banAppeal?.policy ?? null;
  if (errorCode) {
    switch (errorCode) {
      case UNDERAGE_USER_CREATION_ERROR_CODE:
      case UNDERAGE_AUTH_ERROR_CODE:
      case UNDERAGE_ONBOARDING_ERROR_CODE:
      case UNDERAGE_ONBOARDING_NOT_APPEALABLE_CODE: {
        const underageBan = response?.error?.banReason?.underageBan;
        const underageTtl =
          response?.error?.data?.underage_ttl_duration_ms ?? // V2 API responses
          underageBan?.underageTtlDurationMs?.value; // V3/Proto responses
        const underageToken =
          response?.error?.data?.underage_token ?? underageBan?.underageToken?.value;
        const isOnboarding =
          response?.error?.data?.is_onboarding ?? underageBan?.isOnboarding ?? false;
        const isAppealable = errorCode === UNDERAGE_AUTH_ERROR_CODE;
        throw new UnderageUserError(
          status,
          error,
          underageTtl,
          underageToken,
          isOnboarding,
          isAppealable,
          policy
        );
      }
      case BLACKLIST_ERROR_CODE:
        throw new BlacklistedUserError(status, error, appealToken, policy);
      case SELFIE_CHALLENGE_V2_ERROR_CODE:
        throw new SelfieChallengeV2Error(status, error, selfieChallengeToken, selfieStatus);
      case BANNED_ERROR_CODE:
        throw new BannedUserError(status, error, appealToken);
      case UNDER_REVIEW_CODE:
        throw new UnderReviewUserError(status, error);
      case APPEALABLE_BANNED_CODE: {
        const appealableBanData = get(response, ['error', 'banReason', 'banAppeal']);
        const challengeToken = get(appealableBanData, 'challengeToken');
        const refreshToken = get(appealableBanData, 'refreshToken');
        const arkoseDataBlob = get(appealableBanData, 'arkoseDataBlob');
        if (challengeToken) {
          throw new AppealableBannedUserError(
            status,
            error,
            challengeToken,
            refreshToken,
            arkoseDataBlob
          );
        }
        // if no token returned, fallback to warden
        throw new BannedUserError(status, error, appealToken);
      }
      case RSO_BANNED_CODE:
        throw new RSOBannedUserError(status, error);
      case RATE_LIMITED_CODE:
        throw new RateLimitedError(status, error);
      default: {
        if (status >= 400 && status < 500) {
          throw new BadRequestAPIError(status, error);
        }
      }
    }
  }

  if (response) {
    if (status >= 400 && status < 500) {
      // Handle all 4xx responses here
      trackErrorResponse(response);
      if (status === 403 && get(response, ['data', 'logout']) === true) {
        debugShowError('API return 403, force logout');
        throw new LoggedOutTinderError();
      }
      throw new BadRequestAPIError(status, error);
    } else if (status >= 500 && status < 599) {
      // Handle all 5xx responses here
      trackErrorResponse(response);
      throw new ServerSideAPIError(status, error);
    }
  }

  const isOffline = yield* select(getIsOffline);
  if (isOffline) {
    yield* put(appOnline());
  }

  return response;
}

export function* reauthServiceExecutor(fn, ...args) {
  let response;
  try {
    response = yield* call(fn, ...args);
  } catch (e) {
    debugShowError(e);
    throw e;
  }

  const status = get(response, 'status') || get(response, ['meta', 'status']);
  // Special error cases for re-auth only
  if (status === 401 || status === 400) {
    // Sign out user, they need to manually auth
    // We throw this here to prevent another reauth attempt
    throw new UnauthorizedAPIError();
  }

  return yield* call(handleResponseErrors, response, status);
}

export function* serviceExecutor(fn, ...args) {
  let response;
  try {
    response = yield* call(fn, ...args);
  } catch (e) {
    debugShowError(e);
    throw e;
  }

  const status = get(response, 'status') || get(response, ['meta', 'status']);
  // Reauth
  if (status === 401) {
    const userLoggedIn = yield* select(getIsUserLoggedIn);
    const isRemoteView = yield* select(getIsRemote);
    if (userLoggedIn) {
      if (!(yield* select(getIsReauthing))) {
        yield* put(reauth());
      }
      yield* take(LOGIN_SUCCESS);
      return yield* call(serviceExecutor, fn, args);
    }
    // for whatever reason we are calling API on un-auth and got 401, force logout
    const isLoggingOut = yield* select(getIsUserLoggingOut);
    if (!isLoggingOut && !isRemoteView) {
      yield* put(logout(false));
    }

    if (isRemoteView) {
      executeLogEvent(
        new Log({
          message: 'Invalid auth token provided for Remote View',
          shouldSample: false,
        })
      );
    }

    return;
  }

  return yield* call(handleResponseErrors, response, status);
}

export const EXECUTOR_SERVICE = 'EXECUTOR_SERVICE';
export const EXECUTOR_REAUTH = 'EXECUTOR_REAUTH';
export const EXECUTOR_DEFAULT = 'EXECUTOR_DEFAULT'; // Passthrough function for cases where we dont need additional logic

export type ExecutorType =
  | typeof EXECUTOR_SERVICE
  | typeof EXECUTOR_REAUTH
  | typeof EXECUTOR_DEFAULT;

export const errorHandlers = {
  [EXECUTOR_SERVICE]: serviceExecutor,
  [EXECUTOR_REAUTH]: reauthServiceExecutor,
  [EXECUTOR_DEFAULT]: call,
};

export const WARDEN_REDIRECT = {
  [BLACKLISTED_USER_ERROR]: getRoute('warden', {
    paramValues: { tier: 'banned' },
  }),
  [BANNED_USER_ERROR]: getRoute('warden', {
    paramValues: { tier: 'banned' },
  }),
  [UNDER_REVIEW_USER_ERROR]: getRoute('warden', {
    paramValues: { tier: 'underReview' },
  }),
  [RSO_BANNED_USER_ERROR]: getRoute('warden', {
    paramValues: { tier: 'bcBanned' },
  }),
};

// TODO: This will become the new WARDEN_REDIRECT once the custom ban screen is 100% rolled out
export const WARDEN_REDIRECT_CUSTOM_BAN_SCREEN = {
  ...WARDEN_REDIRECT,
  [BLACKLISTED_USER_ERROR]: getRoute('warden', {
    paramValues: {
      tier: 'customBanScreen',
    },
  }),
};

export const getWardenRedirect = (name: string, policy?: BanPolicy) => {
  const isCustomBanScreenEnabled = getLeverValue(LEVER_DSA_CUSTOM_BAN_SCREEN);

  // Check if lever is active and that a policy exists
  const redirects =
    isCustomBanScreenEnabled && policy ? WARDEN_REDIRECT_CUSTOM_BAN_SCREEN : WARDEN_REDIRECT;

  return redirects[name];
};

export const SELFIE_CHALLENGE_REDIRECT = getRoute('selfieChallenge');

export const SELFIE_CHALLENGE_V2_REDIRECT = getRoute('selfieChallengeV2');
